import AntIcon from "@/components/icons/AntIcon";
import { checkHasPathAccess } from "@/utils/menuUtils";
import { stripAccents } from "@/utils/utils";
import { AutoComplete, Empty, Spin } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { BaseOptionType } from "antd/lib/select";
import debounce from "lodash/debounce";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useSearchGlobally } from "../../../common/api/queries";
import { TopAgentType } from "../../../common/enums";
import { hasAllPermissions } from "../../../common/security/utils";
import { AGENT_ROUTE_PATHS } from "../../../modules/agent/paths";
import { formatAgentName } from "../../../modules/agent/utils";
import { useIsCurrentAgentTypePlus, usePermissions } from "../../../modules/auth/hooks";
import { CLIENT_ROUTE_PATHS } from "../../../modules/client/paths";
import { CONTRACT_ROUTE_PATHS } from "../../../modules/contract/paths";
import { FINANCIAL_MEDIATION_ROUTE_PATHS } from "../../../modules/financialmediation/paths";
import { GlobalSearchType } from "../../../modules/globalsearch/enums";
import { NavigationGlobalSearchMap } from "../../../modules/globalsearch/utils";
import { ADMIN_USER_ROUTE_PATHS } from "../../../modules/user/paths";
import { PagesMap } from "../../pages/pagesMap";
import styles from "./GlobalSearch.module.scss";

export const GlobalSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [autocompleteValue, setAutocompleteValue] = useState<string>();
  const [searchValue, setSearchValue] = useState<string>("");

  const accountPermissions = usePermissions();
  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const { data: searchResult, isFetching } = useSearchGlobally({ keyword: searchValue });

  const availablePages = useMemo(
    () =>
      NavigationGlobalSearchMap.filter((item) => {
        if (item.config) {
          return hasAllPermissions(accountPermissions, item.config.permissions);
        } else {
          return checkHasPathAccess(item.path, accountPermissions, isTopAgentPlus ? TopAgentType.PLUS : undefined);
        }
      }),
    [accountPermissions],
  );

  const autocompleteOptions = useMemo(() => {
    const result: DefaultOptionType[] = [];

    if (searchResult?.clients.length) {
      result.push({
        label: t("globalSearch.category.clients"),
        options: searchResult.clients.map((client) => ({
          value: client.id,
          type: GlobalSearchType.CLIENT,
          label: (
            <span className={styles.searchText}>
              <span>{client.aggregatedName}</span>
              <br />
              <span className={styles.searchSubtext}>{client.identifier}</span>
            </span>
          ),
        })),
      });
    }

    if (searchResult?.contracts.length) {
      result.push({
        label: t("globalSearch.category.contracts"),
        options: searchResult.contracts.map((contract) => ({
          value: contract.id,
          type: GlobalSearchType.CONTRACT,
          label: (
            <span className={styles.searchText}>
              <span>
                {contract.contractNumber || contract.secondaryContractNumber} | {contract.policyHolder?.aggregatedName}
              </span>
              <br />
              <span className={styles.searchSubtext}>
                {contract.product.name} | {contract.institution.name}
              </span>
            </span>
          ),
        })),
      });
    }

    if (searchResult?.mediations?.length) {
      result.push({
        label: t("globalSearch.category.financialMediations"),
        options: searchResult.mediations.map((financialMediation) => {
          const secondaryClientAggregatedName = financialMediation.secondaryClient?.aggregatedName;

          return {
            value: financialMediation.id,
            type: GlobalSearchType.FINANCIAL_MEDIATION,
            label: (
              <div className={styles.searchText}>
                <div>
                  {financialMediation.policyHolder.aggregatedName}{" "}
                  {secondaryClientAggregatedName ? `| ${secondaryClientAggregatedName}` : undefined}
                </div>
                <div className={styles.searchSubtext}>
                  {`${t("product.enums.financialSector." + financialMediation.sector)} | ${t("financialMediation.enums.version." + financialMediation.version)}`}
                </div>
              </div>
            ),
          };
        }),
      });
    }

    if (searchResult?.agents.length) {
      result.push({
        label: t("globalSearch.category.agents"),
        options: searchResult.agents.map((agent) => ({
          value: agent.id,
          type: GlobalSearchType.AGENT,
          label: (
            <span className={styles.searchText}>
              <span>{formatAgentName(agent)}</span>
              <br />
              <span className={styles.searchSubtext}>{agent.identifier}</span>
            </span>
          ),
        })),
      });
    }

    if (searchResult?.users.length) {
      result.push({
        label: t("globalSearch.category.users"),
        options: searchResult.users.map((user) => ({
          value: user.id,
          type: GlobalSearchType.USER,
          label: (
            <span className={styles.searchText}>
              <span>{user.name}</span>
              <br />
              <span className={styles.searchSubtext}>{user.email || user.emailToConfirm}</span>
            </span>
          ),
        })),
      });
    }

    const strippedKeyword = searchResult?.keyword ? stripAccents(searchResult.keyword).toLowerCase() : undefined;
    const foundPages = strippedKeyword ? availablePages.filter((page) => page.keyword.includes(strippedKeyword)) : [];

    if (foundPages.length) {
      result.push({
        label: t("globalSearch.category.navigation"),
        options: foundPages.map((page) => ({
          value: page.path,
          type: GlobalSearchType.NAVIGATION,
          label: (
            <span className={styles.searchText}>
              <span>{page.config?.title || PagesMap.get(page.path)?.title}</span>
            </span>
          ),
        })),
      });
    }

    return result;
  }, [searchResult, availablePages, t]);

  const handleAutocompleteSearchDebounced = useMemo(
    () =>
      debounce((keyword: string) => {
        setSearchValue(keyword);
      }, 500),
    [],
  );

  const handleAutocompleteSearch = useCallback(
    (value: string) => {
      handleAutocompleteSearchDebounced(value);
    },
    [handleAutocompleteSearchDebounced],
  );

  const handleAutocompleteSelect = (value: string, option: BaseOptionType) => {
    switch (option.type) {
      case GlobalSearchType.CLIENT:
        navigate(generatePath(CLIENT_ROUTE_PATHS.detail.to, { id: value }));
        break;
      case GlobalSearchType.CONTRACT:
        navigate(generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: value }));
        break;
      case GlobalSearchType.AGENT:
        navigate(generatePath(AGENT_ROUTE_PATHS.detail.to, { id: value }));
        break;
      case GlobalSearchType.USER:
        navigate(generatePath(ADMIN_USER_ROUTE_PATHS.detail.to, { id: value }));
        break;
      case GlobalSearchType.NAVIGATION:
        navigate(generatePath(value));
        break;
      case GlobalSearchType.FINANCIAL_MEDIATION:
        navigate(generatePath(FINANCIAL_MEDIATION_ROUTE_PATHS.detail.to, { id: value }));
        break;
    }

    handleAutocompleteClear();
  };

  const handleAutocompleteClear = () => {
    setAutocompleteValue(undefined);
    setSearchValue("");
  };

  return (
    <div className={styles.globalSearch}>
      <AutoComplete
        style={{ width: 400 }}
        allowClear
        size="middle"
        value={autocompleteValue}
        options={autocompleteOptions}
        popupRender={(menu) => {
          return isFetching ? (
            <div className="center-align" style={{ margin: 48 }}>
              <Spin spinning size="large" />
            </div>
          ) : (
            <>{menu}</>
          );
        }}
        placeholder={
          <div className="center-align">
            <AntIcon type="search" style={{ cursor: "pointer", fontSize: 16 }} /> {t("globalSearch.placeholder")}
          </div>
        }
        notFoundContent={<Empty description={t("globalSearch.noData")} />}
        onChange={setAutocompleteValue}
        showSearch={{ onSearch: handleAutocompleteSearch }}
        onSelect={handleAutocompleteSelect}
        onClear={handleAutocompleteClear}
      />
    </div>
  );
};
